import Controller from 'controllers/controller';
import Elements from 'tools/elements';
import jQuery from 'jquery';

class AjaxModalController extends Controller {
    /**
     * Generic class for managing remote modals.
     * 
     * @param app
     * @param {object} options - The following options are required:
     * {
     *     endpoint: '', // The AJAX API endpoint. This class will contact that API endpoint with a single paramter: 'id'
     *                   // the endpoint must find the object of the correct type by the given ID and return the HTML.
     *                   
     *     modal: '',    // The ID of the modal.
     *          
     *     links: '',    // Selector to find links to objects. Each link *must* have an attribute 'data-id' which contains
     *                   // the object's ID.
     * }
     */
    ready(options) {
        /**
         * @property {object} options
         */
        this.options = options;
        
        this.elements = new Elements;

        this.basePathname = document.body.getAttribute('data-url');

        // Store for caching loaded content
        this.contentCache = { };
        this.objectUrlToIdMap = { };

        this.modal = this.app.modalsController.getModal(this.options.modal);

        this.cacheElements();
        this.cacheObjectLinks();
        this.attachEvents();
        this.openDefaultModal();
    }

    cacheElements() {
        this.elements.queryAll('object-links', this.options.links);
    }

    cacheObjectLinks() {
        this.elements.each('object-links', this.cacheObjectLink);
    }

    cacheObjectLink(link) {
        let model = this.getLinkModel(link);

        this.objectUrlToIdMap[model.url] = model.id;
    }

    getLinkModel(link) {
        return {
            url: link.getAttribute('href'),
            id: link.getAttribute('data-id')
        }
    }

    openDefaultModal() {
        this.elements.each('object-links', link => {
            if (link.classList.contains('open-by-default')) {
                this.openObject(this.objectUrlToIdMap[link.getAttribute('href')]);
            }
        });
    }

    attachEvents() {
        this.elements.each('object-links', link => link.addEventListener('click', this.onObjectLinkClick));
        this.app.modalsController.on(`pd-modal-${this.options.modal}-closed`, this.onObjectModalClosed);
        this.app.historyController.on('change', this.onHistoryChange);
    }

    onObjectModalClosed(manualClose) {
        if (manualClose) {
            this.app.historyController.push(this.basePathname);
        }
    }

    onHistoryChange(state) {
        if (this.objectUrlToIdMap[state.location.pathname]) {
            this.openObject(this.objectUrlToIdMap[state.location.pathname]);
        } else {
            this.modal.close();
        }
    }

    onObjectLinkClick(event) {
        event.preventDefault();

        let model = this.getLinkModel(event.currentTarget);

        this.app.historyController.push(model.url);

        this.openObject(model.id);
    }

    async openObject(id) {
        this.modal.open();

        this.clearModalContent();

        // Set loading class
        this.modal.elements.get('modal').classList.add('is-loading');

        this.setModalContent(
            // Either load via AJAX or from memory-cache
            await this.getContent(id)
        );

        this.modal.elements.get('modal').classList.remove('is-loading');
    }

    /**
     * Clears the modal content and destroys any gallery instances
     */
    clearModalContent() {
        let gallery = this.modal.elements.get('modal').querySelector('.fading-gallery'),
            instance = this.app.fadingGalleriesController.getInstance(gallery);

        // We must destroy old Fading gallery instance
        if (instance) {
            instance.destroy();
        }

        // Empty the modal
        this.modal.setContent('');
    }

    /**
     * Set the modal content and initialised any new fading galleries.
     *
     * @param content
     */
    setModalContent(content) {
        this.modal.setContent(content);

        // Emit dom change to trigger higher-level controllers to re-init and find new components. In this case, the
        // fading gallery will find new galleries.
        this.app.emitDomChange();
        this.app.emitResize();
    }

    async loadContent(id) {
        let content = await jQuery.get({
            url: APP_ROOT + 'ajax/',
            data: {
                endpoint: this.options.endpoint,
                id: id
            }
        });

        // Cache the content so that subsequant opens are faster
        this.contentCache[id] = content;

        return content;
    }

    async getContent(id) {
        return this.contentCache[id] || await this.loadContent(id);
    }
}

export default AjaxModalController;


// WEBPACK FOOTER //
// ./site/templates/resources/src/js/controllers/ajax-modal.js